import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, takeUntil, filter } from 'rxjs';
import { LoggingService } from '../logging/logging.service';


/**
 * Queue Worker Facade Service
 *
 * This service manages a Web Worker that handles message queue persistence with IndexedDB.
 * It eliminates the FIN-0028 data loss bug by persisting messages immediately when queued.
 *
 * FIXES FIN-0028:
 * - BEFORE: In-memory queue → Lost on page refresh
 * - AFTER: IndexedDB → Persists across sessions
 *
 * PERFORMANCE IMPROVEMENT:
 * - BEFORE: localStorage writes block main thread (3-10ms each)
 * - AFTER: IndexedDB operations in worker thread (0ms main thread blocking)
 */

interface QueuedMessage {
  id: string;
  content: string;
  conversationId: string;
  timestamp: number;
  status: 'pending' | 'sending' | 'sent' | 'failed';
  retryCount: number;
  createdAt: number;
  destination: string;
}

interface WorkerMessage {
  type: 'DB_READY' | 'ENQUEUED' | 'MARKED_SENT' | 'MARKED_FAILED' | 'PENDING_MESSAGES' | 'DUPLICATE_CHECK' | 'CLEANUP_DONE' | 'ERROR';
  data?: any;
}

@Injectable({ providedIn: 'root' })
export class QueueWorkerFacadeService implements OnDestroy {
  // ══════════════════════════════════════════════════════════════════════════════
  // PRIVATE FIELDS
  // ══════════════════════════════════════════════════════════════════════════════

  private worker: Worker | null = null;
  private readonly destroy$ = new Subject<void>();

  // State observables
  private readonly dbReadySubject = new BehaviorSubject<boolean>(false);
  private readonly queueSizeSubject = new BehaviorSubject<number>(0);
  private readonly pendingMessagesSubject = new Subject<QueuedMessage[]>();
  private readonly errorSubject = new Subject<any>();

  // ══════════════════════════════════════════════════════════════════════════════
  // CONSTRUCTOR
  // ══════════════════════════════════════════════════════════════════════════════

  constructor(private readonly log: LoggingService) {
    this.log.info('QueueWorkerFacade', '🚀 Initializing Queue Worker Facade');
    // Initialize worker (async, but don't block constructor)
    this.initializeWorker().catch(error => {
      this.log.error('QueueWorkerFacade', 'Failed to initialize worker:', error);
    });
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // PUBLIC API
  // ══════════════════════════════════════════════════════════════════════════════

  /** Observable streams */
  get dbReady$(): Observable<boolean> {
    return this.dbReadySubject.asObservable();
  }

  get queueSize$(): Observable<number> {
    return this.queueSizeSubject.asObservable();
  }

  get pendingMessages$(): Observable<QueuedMessage[]> {
    return this.pendingMessagesSubject.asObservable();
  }

  get errors$(): Observable<any> {
    return this.errorSubject.asObservable();
  }

  /** Getters */
  isDbReady(): boolean {
    return this.dbReadySubject.value;
  }

  getQueueSize(): number {
    return this.queueSizeSubject.value;
  }

  /**
   * Enqueue a message for sending
   * Persists to IndexedDB immediately to prevent data loss
   * WAITS for DB to be ready if not yet initialized (fixes race condition)
   */
  async enqueue(message: {
    id: string;
    content: string;
    conversationId: string;
    timestamp: number;
    destination: string;
  }): Promise<void> {
    // CRITICAL: Wait for DB to be ready before enqueuing
    if (!this.isDbReady()) {
      this.log.warn('QueueWorkerFacade', `DB not ready yet, waiting... (message ${message.id})`);

      // Wait for DB to become ready (with 5 second timeout)
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Timeout waiting for Queue Worker DB to initialize'));
        }, 5000);

        const sub = this.dbReady$
          .pipe(
            filter(Boolean),
            takeUntil(this.destroy$)
          )
          .subscribe(() => {
            clearTimeout(timeout);
            sub.unsubscribe();
            this.log.info('QueueWorkerFacade', `DB ready, proceeding with enqueue (message ${message.id})`);
            resolve();
          });
      });
    }

    this.log.debug('QueueWorkerFacade', `Enqueuing message ${message.id}`);

    this.sendToWorker({
      type: 'ENQUEUE',
      payload: message
    });
  }

  /**
   * Mark a message as sent
   * Removes from queue and adds to sent IDs for deduplication
   */
  markSent(messageId: string): void {
    this.log.debug('QueueWorkerFacade', `Marking message ${messageId} as sent`);

    this.sendToWorker({
      type: 'MARK_SENT',
      payload: { messageId }
    });
  }

  /**
   * Mark a message as failed
   * Increments retry count for later retry
   */
  markFailed(messageId: string): void {
    this.log.debug('QueueWorkerFacade', `Marking message ${messageId} as failed`);

    this.sendToWorker({
      type: 'MARK_FAILED',
      payload: { messageId }
    });
  }

  /**
   * Get all pending/failed messages for retry
   * Call this when connection is established
   */
  getPendingMessages(): void {
    this.log.debug('QueueWorkerFacade', 'Requesting pending messages');

    this.sendToWorker({
      type: 'GET_PENDING',
      payload: {}
    });
  }

  /**
   * Check if a message was already sent (deduplication)
   */
  isDuplicate(messageId: string): Promise<boolean> {
    return new Promise((resolve) => {
      const sub = this.pendingMessagesSubject
        .pipe(takeUntil(this.destroy$))
        .subscribe((data: any) => {
          if (data && data.messageId === messageId && data.isDuplicate !== undefined) {
            sub.unsubscribe();
            resolve(data.isDuplicate);
          }
        });

      this.sendToWorker({
        type: 'IS_DUPLICATE',
        payload: { messageId }
      });
    });
  }

  /**
   * Cleanup old sent IDs (older than 24 hours)
   * Call this periodically to keep database size manageable
   */
  cleanup(): void {
    this.log.debug('QueueWorkerFacade', 'Triggering cleanup of old sent IDs');

    this.sendToWorker({
      type: 'CLEANUP',
      payload: {}
    });
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // PRIVATE METHODS
  // ══════════════════════════════════════════════════════════════════════════════

  /**
   * Initialize Queue Worker
   *
   * CRITICAL: Angular's esbuild ONLY detects workers when using EXACT pattern:
   *   new Worker(new URL('path', import.meta.url))
   *
   * ANY deviation (storing in variable, using static field, etc.) breaks detection.
   * See: https://github.com/angular/angular-cli/issues/28078
   *
   * BROWSER LIMITATION: Workers have stricter CORS requirements than regular fetch.
   * Even with proper CORS headers, browsers block cross-origin Worker() constructor.
   * Workaround: Fetch via AJAX (uses CORS), create Blob URL, load Worker from Blob.
   */
  private async initializeWorker(): Promise<void> {
    if (typeof Worker === 'undefined') {
      this.log.error('QueueWorkerFacade', 'Web Workers not supported in this browser!');
      throw new Error('Web Workers not supported');
    }

    try {
      // Attempt direct worker creation - Angular rewrites URL to worker-HASH.js
      this.worker = new Worker(
        new URL('../../workers/queue.worker.ts', import.meta.url),
        { type: 'module' }
      );

      this.worker.onmessage = (event: MessageEvent<WorkerMessage>) => {
        this.handleWorkerMessage(event.data);
      };

      this.worker.onerror = (error) => {
        this.log.error('QueueWorkerFacade', 'Worker error:', error);
        this.errorSubject.next({ message: 'Worker error', error });
      };

      this.log.info('QueueWorkerFacade', '✅ Worker initialized');

      // Initialize IndexedDB
      this.sendToWorker({ type: 'INIT', payload: {} });
    } catch (error: any) {
      // SecurityError: Browsers block cross-origin Workers even with CORS headers
      // Extract the worker URL from the error message (Angular already rewrote it)
      const urlMatch = error.message?.match(/Script at '([^']+)'/);
      const workerUrl = urlMatch?.[1];

      if (!workerUrl) {
        this.log.error('QueueWorkerFacade', 'Failed to extract worker URL from error:', error);
        throw error;
      }

      this.log.info('QueueWorkerFacade', `Direct creation failed (${error.name}), using Blob URL fallback`);

      try {
        // Fetch via AJAX (CORS allowed), create Blob, load from Blob URL
        this.worker = await this.createCrossOriginWorker(workerUrl);

        this.worker.onmessage = (event: MessageEvent<WorkerMessage>) => {
          this.handleWorkerMessage(event.data);
        };

        this.worker.onerror = (error) => {
          this.log.error('QueueWorkerFacade', 'Worker error:', error);
          this.errorSubject.next({ message: 'Worker error', error });
        };

        this.log.info('QueueWorkerFacade', '✅ Worker initialized (Blob URL)');

        // Initialize IndexedDB
        this.sendToWorker({ type: 'INIT', payload: {} });
      } catch (blobError) {
        this.log.error('QueueWorkerFacade', 'Blob URL fallback also failed:', blobError);
        throw blobError;
      }
    }
  }

  /**
   * Create worker from cross-origin script using Blob URL
   *
   * Browsers block cross-origin Workers even with CORS headers, but allow
   * cross-origin fetch(). This fetches the worker via AJAX, creates a Blob,
   * and loads the Worker from the Blob URL (same-origin).
   */
  private async createCrossOriginWorker(url: string): Promise<Worker> {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch worker: ${response.status} ${response.statusText}`);
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      return new Worker(blobUrl, { type: 'module' });
    } catch (error) {
      this.log.error('QueueWorkerFacade', 'Failed to create cross-origin worker:', error);
      throw error;
    }
  }

  /**
   * Handle messages FROM worker
   */
  private handleWorkerMessage(message: WorkerMessage): void {
    switch (message.type) {
      case 'DB_READY':
        this.dbReadySubject.next(true);
        this.log.info('QueueWorkerFacade', '✅ IndexedDB ready');
        break;

      case 'ENQUEUED':
        if (message.data && message.data.queueSize !== undefined) {
          this.queueSizeSubject.next(message.data.queueSize);
          this.log.debug('QueueWorkerFacade', `Message enqueued. Queue size: ${message.data.queueSize}`);
        }
        break;

      case 'MARKED_SENT':
        if (message.data && message.data.queueSize !== undefined) {
          this.queueSizeSubject.next(message.data.queueSize);
          this.log.debug('QueueWorkerFacade', `Message marked sent. Queue size: ${message.data.queueSize}`);
        }
        break;

      case 'MARKED_FAILED':
        this.log.warn('QueueWorkerFacade', `Message marked failed:`, message.data);
        break;

      case 'PENDING_MESSAGES':
        if (message.data && message.data.messages) {
          this.log.info('QueueWorkerFacade', `Retrieved ${message.data.messages.length} pending messages`);
          this.pendingMessagesSubject.next(message.data.messages);
        }
        break;

      case 'DUPLICATE_CHECK':
        // Forward to promise resolver (handled in isDuplicate method)
        this.pendingMessagesSubject.next(message.data as any);
        break;

      case 'CLEANUP_DONE':
        this.log.info('QueueWorkerFacade', `Cleanup complete:`, message.data);
        break;

      case 'ERROR':
        this.log.error('QueueWorkerFacade', 'Worker error:', message.data);
        this.errorSubject.next(message.data);
        break;

      default:
        this.log.warn('QueueWorkerFacade', 'Unknown worker message type:', message);
    }
  }

  /**
   * Send message TO worker
   */
  private sendToWorker(message: any): void {
    if (!this.worker) {
      this.log.error('QueueWorkerFacade', 'Cannot send to worker - worker not initialized');
      return;
    }

    this.worker.postMessage(message);
  }

  /**
   * Angular lifecycle cleanup
   */
  ngOnDestroy(): void {
    this.log.info('QueueWorkerFacade', 'Service destroying');

    this.destroy$.next();
    this.destroy$.complete();

    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }

    this.dbReadySubject.complete();
    this.queueSizeSubject.complete();
    this.pendingMessagesSubject.complete();
    this.errorSubject.complete();
  }
}
